import React from 'react';
import { TouchableOpacity, View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useColors, ThemeColors } from '../hooks/useColors';
import { spacing, typography } from '../theme';
import { Notification } from '../store/slices/notificationsSlice';
import { supabase } from '../lib/supabase';
import { findOrCreateChat } from '../utils/chatUtils';

interface NotificationCardProps {
  notification: Notification;
  onPress?: () => void;
  onMarkAsRead?: () => void;
  onDelete?: () => void;
}

export function NotificationCard({ 
  notification, 
  onPress, 
  onMarkAsRead, 
  onDelete 
}: NotificationCardProps) {
  const navigation = useNavigation();
  const colors = useColors();
  const styles = createStyles(colors, notification.status === 'unread');

  const getNotificationIcon = (type: Notification['type'] | string) => {
    const iconColor = "#FFFFFF";
    const bgColor = "#000000";
    
    switch (type) {
      case 'order':
        return { name: 'cart-outline', color: iconColor, bg: bgColor };
      case 'message':
      case 'chat_message':
        return { name: 'chatbubble-outline', color: iconColor, bg: bgColor };
      case 'price_drop':
        return { name: 'trending-down-outline', color: iconColor, bg: bgColor };
      default:
        return { name: 'notifications-outline', color: iconColor, bg: bgColor };
    }
  };

  const formatTime = (dateString: string) => {
    // Validate input
    if (!dateString) {
      return 'Unknown time';
    }

    const date = new Date(dateString);
    const now = new Date();
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const diffInMs = now.getTime() - date.getTime();
    
    // Handle future dates (should not happen but just in case)
    if (diffInMs < 0) {
      return 'Just now';
    }

    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInHours < 1) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks}w ago`;
    } else {
      const months = Math.floor(diffInDays / 30);
      return `${months}mo ago`;
    }
  };


  const handlePress = async () => {
    if (onPress) {
      onPress();
    } else {
      // Navigate based on notification type
      await handleNotificationNavigation();
    }
    
    // Mark as read if it's unread
    if (notification.status === 'unread' && onMarkAsRead) {
      onMarkAsRead();
    }
  };

  const handleNotificationNavigation = async () => {
    const { type, data } = notification;
    
    switch (type) {
      case 'order':
        if (data?.orderId) {
          // @ts-ignore
          navigation.navigate('OrderDetails', { orderId: data.orderId });
        }
        break;
      case 'message':
      case 'chat_message':
        if (data?.chatId) {
          // Verify the chat exists before navigating
          const { data: chatExists, error } = await supabase
            .from('chats')
            .select('id')
            .eq('id', data.chatId)
            .maybeSingle();
          
          if (error || !chatExists) {
            console.warn('Chat no longer exists:', data.chatId);
            // Navigate to chat list instead
            // @ts-ignore
            navigation.navigate('Chats');
            return;
          }
          // @ts-ignore
          navigation.navigate('Chat', { id: data.chatId });
        }
        break;
      case 'price_drop':
        if (data?.productId) {
          // @ts-ignore
          navigation.navigate('ProductDetails', { productId: data.productId });
        }
        break;
      default:
        break;
    }
  };

  const handleMessageSeller = async (event: any) => {
    event.stopPropagation(); // Prevent bubbling to main press handler
    console.log('🔔 [NOTIFICATION-CARD] Message Seller button pressed');
    
    // Mark as read if unread
    if (notification.status === 'unread' && onMarkAsRead) {
      onMarkAsRead();
    }

    try {
      const vendorId = notification.data?.vendor_id;
      if (!vendorId) {
        console.error('No vendor ID found in notification data');
        return;
      }

      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.error('No authenticated user found');
        return;
      }

      // Get vendor's user_id
      const { data: vendorData, error: vendorError } = await supabase
        .from('vendors')
        .select('user_id')
        .eq('id', vendorId)
        .single();

      if (vendorError) {
        console.error('Error fetching vendor user_id:', vendorError);
        return;
      }

      // Find or create chat using user IDs (not vendor_id) to ensure consistent chat grouping
      const chatId = await findOrCreateChat(session.user.id, vendorData.user_id, vendorId);
      
      console.log('Navigating to chat:', chatId);
      // @ts-ignore
      navigation.navigate('Chat', { id: chatId });
    } catch (error) {
      console.error('Error handling message seller:', error);
    }
  };

  const getNotificationMessage = () => {
    return notification.message;
  };

  const icon = getNotificationIcon(notification.type);

  // Determine if this notification should show the "Message Seller" button
  const shouldShowMessageButton = notification.data?.vendor_id && 
    ['order', 'price_drop'].includes(notification.type);

  return (
    <TouchableOpacity 
      style={styles.container} 
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        <View style={[styles.iconContainer, { backgroundColor: icon.bg }]}>
          <Ionicons 
            name={icon.name as any} 
            size={22} 
            color={icon.color} 
          />
        </View>
        
        <View style={styles.textContainer}>
          <View style={styles.headerRow}>
            <Text style={styles.message} numberOfLines={2}>
              {getNotificationMessage()}
            </Text>
            {notification.status === 'unread' && (
              <View style={styles.unreadDot} />
            )}
          </View>
          
          {notification.data?.notes && (
            <Text style={styles.notes} numberOfLines={1}>
              {notification.data.notes}
            </Text>
          )}
          
          <View style={styles.footerRow}>
            <Text style={styles.time}>
              {formatTime(notification.created_at)}
            </Text>
            
            {shouldShowMessageButton && (
              <TouchableOpacity 
                style={styles.messageSellerButton}
                onPress={handleMessageSeller}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons 
                  name="chatbubble-ellipses-outline" 
                  size={14} 
                  color={colors.primary} 
                />
                <Text style={styles.messageSellerText}>Message Seller</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        
        {onDelete && (
          <TouchableOpacity 
            style={styles.deleteButton}
            onPress={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons 
              name="close-outline" 
              size={20} 
              color={colors.textLight} 
            />
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );
}

const createStyles = (colors: ThemeColors, isUnread: boolean) => StyleSheet.create({
  container: {
    backgroundColor: colors.surface,
    marginBottom: spacing.md,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: isUnread ? colors.primary + '30' : colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
    overflow: 'hidden',
  },
  content: {
    flexDirection: 'row',
    padding: spacing.md,
    alignItems: 'center',
    minHeight: 88,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  textContainer: {
    flex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  message: {
    flex: 1,
    ...typography.body,
    color: colors.text,
    fontWeight: isUnread ? '600' : '400',
    marginBottom: 4,
    lineHeight: 20,
    marginRight: spacing.xs,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary,
    marginTop: 6,
  },
  notes: {
    ...typography.caption,
    color: colors.textSecondary,
    fontStyle: 'italic',
    marginBottom: spacing.xs,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  time: {
    ...typography.caption,
    color: colors.textLight,
  },
  messageSellerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 16,
    gap: 4,
  },
  messageSellerText: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '600',
    fontSize: 11,
  },
  deleteButton: {
    padding: spacing.xs,
    marginLeft: spacing.xs,
  },
});